import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import dayjs from "dayjs";
import uzTranslations from "./translations/uz.json";
import ruTranslations from "./translations/ru.json";
import enTranslations from "./translations/en.json";
import qrTranslations from "./translations/qr.json";
import { LANGUAGE } from "src/static/const";
import { termLabels } from "src/config/termConfig";

type Language = "uz" | "ru" | "en" | "qr";

interface Translations {
  [key: string]: any;
}

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Translations> = {
  uz: {
    ...uzTranslations,
    quizzes: {
      ...uzTranslations.quizzes,
      title:               termLabels.plural,
      newQuizzes:          termLabels.newPlural,
      inProgress:          termLabels.inProgress,
      completed:           termLabels.completed,
      startQuiz:           termLabels.start,
      continueQuiz:        termLabels.continue,
      selectLanguageModal: termLabels.start,
    },
    menu: {
      ...uzTranslations.menu,
      quizzes: termLabels.plural,
    },
  },
  ru: ruTranslations,
  en: enTranslations,
  qr: qrTranslations,
};

export type { Language };

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useTranslation must be used within I18nProvider");
  }
  return context;
};

interface I18nProviderProps {
  children: ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLang = localStorage.getItem(LANGUAGE);
    const validLanguages: Language[] = ["uz", "ru", "en", "qr"];
    return (validLanguages.includes(savedLang as Language) ? savedLang : "uz") as Language;
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE, lang);
    // Update dayjs locale
    dayjs.locale(lang);
  };
  
  // Initialize dayjs locale on mount
  useEffect(() => {
    dayjs.locale(language);
  }, [language]);

  const t = (key: string): string => {
    const keys = key.split(".");
    let value: any = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k];
      } else {
        return key; // Fallback to key if translation not found
      }
    }
    
    return typeof value === "string" ? value : key;
  };

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
};
